import {createApi} from '@reduxjs/toolkit/query/react'
import {omit} from 'ramda'

import {dataDogLogEvent} from '@vio/core/src/analytics/components/DataDogClient'
import {getUserSessionService} from '@vio/core/src/auth/services'
import {logger} from '@vio/core/src/utils/logging/logger'
import {
  fetchBaseQuery,
  FetchBaseQueryFn
} from '@vio/core/src/utils/network/fetchBaseQuery'

import {CreatePriceWatch, PriceWatchMetric, PriceWatchPayload} from '../types'

const MEMBERSHIP_API_URL = process.env.NEXT_PUBLIC_MEMBERSHIP_API_ENDPOINT ?? ''

const HEADERS = {
  'Content-Type': 'application/json'
}

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  try {
    const {accessToken} = await getUserSessionService()

    if (!accessToken) return HEADERS

    return {
      ...HEADERS,
      Authorization: `Bearer ${accessToken}`
    }
  } catch {
    return HEADERS
  }
}

const baseQueryWithErrorLogging: FetchBaseQueryFn = async (
  args,
  api,
  extraOptions
) => {
  const baseQuery = fetchBaseQuery({baseUrl: MEMBERSHIP_API_URL})
  const result = await baseQuery(args, api, extraOptions)

  if (result.error) {
    const {error} = result
    const errorMetadata = {
      url: `${MEMBERSHIP_API_URL}${args.url}`,
      method: args.method,
      status: error.status,
      error: error.data
    }
    dataDogLogEvent('PriceWatch.APIError', errorMetadata, 'error' as const)
    logger.error('PriceWatch API error:', {
      err: error,
      ...errorMetadata
    })
  }

  return result
}

export const priceWatchApi = createApi({
  reducerPath: 'priceWatchApi',
  baseQuery: baseQueryWithErrorLogging,
  tagTypes: ['PriceWatchTrackings'],
  endpoints: builder => ({
    cancelPriceWatch: builder.mutation<
      string,
      {priceWatchId: string; userId: string; remove?: boolean}
    >({
      async queryFn(
        {priceWatchId, remove = false},
        _queryApi,
        _extraOptions,
        fetchWithBQ
      ) {
        const headers = await getAuthHeaders()
        const result = await fetchWithBQ({
          url: 'v2/price-watch/cancel',
          method: 'POST',
          headers,
          data: {priceWatchId, remove}
        })
        return result.data
          ? {data: result.data as string}
          : {error: result.error!}
      },
      async onQueryStarted(
        {priceWatchId, userId, remove},
        {dispatch, queryFulfilled}
      ) {
        const patchResult = dispatch(
          priceWatchApi.util.updateQueryData(
            'getUserPriceWatchTrackings',
            {userId},
            draft => {
              if (remove)
                return draft.filter(
                  tracking => tracking.priceWatchId !== priceWatchId
                )

              const tracking = draft.find(t => t.priceWatchId === priceWatchId)
              if (tracking) tracking.metric = PriceWatchMetric.Paused
              return draft
            }
          )
        )
        try {
          await queryFulfilled
          dispatch(priceWatchApi.util.invalidateTags(['PriceWatchTrackings']))
        } catch {
          patchResult.undo()
        }
      }
    }),
    getUserPriceWatchTrackings: builder.query<
      PriceWatchPayload[],
      {userId: string}
    >({
      async queryFn(_arg, _queryApi, _extraOptions, fetchWithBQ) {
        const headers = await getAuthHeaders()
        const result = await fetchWithBQ({
          url: 'v2/price-watch/trackings',
          method: 'GET',
          headers
        })
        return result.data
          ? {data: result.data as PriceWatchPayload[]}
          : {error: result.error!}
      },
      providesTags: ['PriceWatchTrackings']
    }),
    createAnonymousPriceWatchTracking: builder.mutation<
      PriceWatchPayload,
      CreatePriceWatch & {userId: string}
    >({
      async queryFn(body, _queryApi, _extraOptions, fetchWithBQ) {
        const headers = await getAuthHeaders()
        const result = await fetchWithBQ({
          url: 'v2/price-watch/create',
          method: 'POST',
          headers,
          data: omit(['userId'], body)
        })
        return result.data
          ? {data: result.data as PriceWatchPayload}
          : {error: result.error!}
      },
      async onQueryStarted(newTracking, {dispatch, queryFulfilled}) {
        const patchResult = dispatch(
          priceWatchApi.util.updateQueryData(
            'getUserPriceWatchTrackings',
            {userId: newTracking.userId},
            (draft: PriceWatchPayload[] = []): PriceWatchPayload[] => {
              const matchesItinerary = (t: PriceWatchPayload) =>
                t.hotelId === newTracking.hotelId &&
                t.checkIn === newTracking.checkIn &&
                t.nights === newTracking.nights &&
                t.currency === newTracking.currency &&
                t.rooms === newTracking.rooms

              // A room-level create resumes the tracking with the same
              // masterId, never an itinerary-level sibling — and vice versa
              const existingEntry = draft.find(
                (t: PriceWatchPayload) =>
                  matchesItinerary(t) &&
                  (newTracking.room?.masterId
                    ? t.room?.masterId === newTracking.room.masterId
                    : !t.room)
              )

              if (existingEntry) {
                existingEntry.metric = PriceWatchMetric.Resumed
                return draft
              }

              const optimisticEntry = {
                ...newTracking,
                metric: PriceWatchMetric.Created
              }
              draft.push(optimisticEntry as unknown as PriceWatchPayload)

              return draft
            }
          )
        )

        try {
          await queryFulfilled
          // Invalidate the cache to refetch with server data
          dispatch(priceWatchApi.util.invalidateTags(['PriceWatchTrackings']))
        } catch {
          patchResult.undo()
        }
      }
    })
  })
})

export const {
  useCancelPriceWatchMutation,
  useGetUserPriceWatchTrackingsQuery,
  useCreateAnonymousPriceWatchTrackingMutation
} = priceWatchApi
